import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

// Services
import { ClientAuthService } from '../client-auth.service';
import { ClientDashboardAuthService } from '../client-dashboard-auth.service';

// Interfaces/Models
import { IJsonApiResponse } from '@interfaces/api/IJsonApiResponse';
import { IClientRole } from '@interfaces/database-models/IClientRole';
import { IListRequestParams } from '@interfaces/api/IListRequestParams';
import { IListQueryResponse } from '@interfaces/api/IListQueryResponse';
import { ICreateDashboardUserRoleBody } from '@api-services/interfaces/ICreateDashboardUserRoleBody';
import { IEditDashboardUserRoleBody } from '@api-services/interfaces/IEditDashboardUserRoleBody';

// Enums
import { ClientPermission } from '@enums/ClientPermission';

// Statics
import { HttpUtility } from '../../static-classes/HttpUtility';

/**
 * We pass the dashboardId into the GetApiBasePath from all functions
 * because these functions may be run from a scenario where the current dashboard isn't yet
 * loaded into memory, but does exist in the route parameters.
 */

@Injectable({
  providedIn: 'root'
})
export class DashboardUserRolesService {
  private readonly HttpClient = inject(HttpClient);
  private readonly AuthService = inject(ClientAuthService);
  private readonly ClientDashboardAuthService = inject(ClientDashboardAuthService);

  private GetApiBasePath(dashboardId: number): string {
    return `/api/v1/client/dashboard/${dashboardId}/roles`;
  }
  
  public GetDashboardUserRole(dashboardId: number, id: number): Observable<IJsonApiResponse<IClientRole>>{
    return this.HttpClient.get<IJsonApiResponse<IClientRole>>(environment.API_HOST + this.GetApiBasePath(dashboardId) + `/${id}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  public ListDashboardRoles(dashboardId: number, params: IListRequestParams): Observable<IJsonApiResponse<IListQueryResponse<IClientRole>>> {
    const httpParams = HttpUtility.ObjectToHttpParams(params);
    return this.HttpClient.get<IJsonApiResponse<IListQueryResponse<IClientRole>>>(environment.API_HOST + this.GetApiBasePath(dashboardId), {
      params: httpParams,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  public CreateDashboardUserRole(dashboardId: number, body: ICreateDashboardUserRoleBody): Observable<IJsonApiResponse<IClientRole>>{
    return this.HttpClient.post<IJsonApiResponse<IClientRole>>(environment.API_HOST + this.GetApiBasePath(dashboardId), body, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      },
    });
  }

  public EditDashboardUserRole(dashboardId: number, roleId: number, body: IEditDashboardUserRoleBody): Observable<IJsonApiResponse<IClientRole>>{
    return this.HttpClient.patch<IJsonApiResponse<IClientRole>>(environment.API_HOST + this.GetApiBasePath(dashboardId) + `/${roleId}`, body, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      },
    });
  }

  public DeleteDashboardUserRole(dashboardId: number, roleId: number): Observable<IJsonApiResponse<void>>{
    return this.HttpClient.delete<IJsonApiResponse<void>>(environment.API_HOST +  this.GetApiBasePath(dashboardId) + `/${roleId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Fetches the role for the current account for the current dashboard.
   * Fails with 404 if there is no role for the current account in the current dashboard
   * (this is a rare situation and will never be normal).
   */
  public GetRoleForCurrentAccount(dashboardId: number): Observable<IJsonApiResponse<IClientRole>> {
    return this.HttpClient.get<IJsonApiResponse<IClientRole>>(environment.API_HOST + this.GetApiBasePath(dashboardId) + `/current-account-role`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      },
    });
  }

  /**
   * Returns true if the currently authenticated client and their role
   * for the current dashboard satisfies the provided permissions.
   */
  public ClientHasPermissions(perms: ClientPermission[]): boolean {
    const currentDashboardRole = this.AuthService.CurrentClientDashboardRole();
    if (currentDashboardRole === undefined) {
      throw "Role never populated. The route must be protected by a guard that loads the role into the ClientAuthService.";
    }

    if (currentDashboardRole === null){
      return false;
    }

    if (currentDashboardRole.HasAllPermissions){
      return true;
    }

    // Permission verify
    const allRolePermissionEnumCases = currentDashboardRole.Permissions.map(permObject => permObject.Permission);
    for (const permToCheck of perms) {
      if (!allRolePermissionEnumCases.includes(permToCheck)){
        return false;
      }
    }

    return true;
  }

  /**
   * Returns true if the currently authenticated client and their role
   * for the current dashboard satisfies any one of the provided permissions.
   * 
   * To check for all permissions, use ClientHasPermissions.
   */
  public ClientHasAnyPermission(perms: ClientPermission[]): boolean {
    const currentDashboardRole = this.AuthService.CurrentClientDashboardRole();
    if (currentDashboardRole === undefined) {
      throw "Role never populated. The route must be protected by a guard that loads the role into the ClientAuthService.";
    }

    if (currentDashboardRole === null){
      return false;
    }

    if (currentDashboardRole.HasAllPermissions){
      return true;
    }

    // Permission verify
    const allRolePermissionEnumCases = currentDashboardRole.Permissions.map(permObject => permObject.Permission);
    for (const permToCheck of perms) {
      if (allRolePermissionEnumCases.includes(permToCheck)){
        return true
      }
    }

    return false;
  }
}
